/**
 * Per-player nano-id auth. Minted at registration, sent in SMS, used on all
 * /s/:playerKey/* routes.
 *
 * The key the Apollo link sends is TAB-LOCAL, deliberately not localStorage.
 * localStorage is shared by every tab on an origin, so opening a second
 * player's link re-pointed every open tab at that key: an older tab kept its
 * own URL and its own cache but started authenticating as the new player —
 * rendering their name and event, and, because the API resolves the player
 * from this header alone, writing scores onto their card. One phone sees many
 * keys (two events in a day, or several days running) and mobile browsers keep
 * tabs alive for days, so it was reachable in ordinary use.
 *
 * A module-level variable lives in the tab's own JS context, so each tab keeps
 * the key its URL carries no matter what other tabs do. There is deliberately
 * NO localStorage fallback: a request that somehow fires before the loader has
 * claimed a key sends no header and fails a guard, which is loud and safe,
 * rather than silently borrowing whichever key another tab stored.
 */

const PLAYER_STORAGE_KEY = 'polf-player-key'

/**
 * The key THIS tab authenticates as. Set by PlayerKeyLoader from the route
 * param before any child renders, so every player query has it.
 */
let activePlayerKey: string | null = null

/** What the Apollo `player-key` link sends. Never another tab's key. */
export const getActivePlayerKey = (): string | null => activePlayerKey

/**
 * Sets the key for this tab, and mirrors it to localStorage as the origin's
 * last-used key. Nothing reads the stored copy today — it is kept for a launch
 * path that has no key in the URL (the PWA manifest's `start_url` is "/"),
 * and must never be used for auth.
 */
export const setPlayerKey = (key: string): void => {
  activePlayerKey = key
  if (typeof window === 'undefined') return
  window.localStorage.setItem(PLAYER_STORAGE_KEY, key)
}

export const clearPlayerKey = (): void => {
  activePlayerKey = null
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(PLAYER_STORAGE_KEY)
}
